import { createSignal, createEffect } from 'solid-js';
import { twclsx } from './twclsx';

enum Theme {
  LIGHT = 'light',
  DARK = 'dark',
}

const initializeTheme = () => {
  if (typeof localStorage !== 'undefined' && localStorage.getItem('theme')) {
    return localStorage.getItem('theme') as Theme;
  }

  if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return Theme.DARK;
  }

  return Theme.LIGHT;
};

const ThemeToggler = () => {
  const [theme, setTheme] = createSignal<Theme>(initializeTheme());

  createEffect(() => {
    const root = document.documentElement;
    if (theme() === Theme.LIGHT) {
      root.classList.remove('dark');
      localStorage.setItem('theme', Theme.LIGHT);
    } else {
      root.classList.add('dark');
      localStorage.setItem('theme', Theme.DARK);
    }
  });

  return (
    <>
      <button
        id="theme-toggle"
        data-tooltip-target="theme-tooltip-toggle"
        type="button"
        class={twclsx(
          'text-gray-500',
          'dark:text-gray-400',
          'hover:bg-gray-100',
          'dark:hover:bg-gray-700',
          'focus:outline-none',
          'focus:ring-4',
          'focus:ring-gray-200',
          'dark:focus:ring-gray-700',
          'rounded-lg',
          'text-sm',
          'mx-2',
          // 'md:mx-2',
        )}
        onClick={() => setTheme(t => (t === Theme.LIGHT ? Theme.DARK : Theme.LIGHT))}
      >
        <svg
          id="theme-toggle-dark-icon"
          class={twclsx('w-5', 'h-5', theme() === Theme.DARK && 'hidden')}
          fill="currentColor"
          viewBox="0 0 20 20"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
        </svg>
        <svg
          id="theme-toggle-light-icon"
          class={twclsx('w-5', 'h-5', theme() === Theme.LIGHT && 'hidden')}
          fill="currentColor"
          viewBox="0 0 20 20"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z"
            fill-rule="evenodd"
            clip-rule="evenodd"
          />
        </svg>
      </button>
      <div
        id="theme-tooltip-toggle"
        role="tooltip"
        class={twclsx(
          'absolute',
          'z-10',
          'invisible',
          'inline-block',
          'px-3',
          'py-2',
          'text-sm',
          'font-medium',
          'text-white',
          'transition-opacity',
          'duration-300',
          'bg-gray-900',
          'rounded-lg',
          'shadow-sm',
          'opacity-0',
          'tooltip',
        )}
      >
        Toggle dark mode
        <div class={twclsx('tooltip-arrow')} data-popper-arrow />
      </div>
    </>
  );
};

export default ThemeToggler;
